import React from 'react';
import { formatCurrency } from 'Core/utils/numberFormat';
import { Column, Modal, Row } from 'Core/components/Layout';
import { generateURL } from 'Core/utils/utils';
import { Alert } from 'Core/components/Alert';
import { OutlineButton, PrimaryButton } from 'Core/components/Button';

function AmountRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-200 py-2 last:border-b-0">
      <span className="text-gray-700">{label}</span>
      <span className={highlight ? 'font-bold text-primary' : 'font-semibold'}>
        {formatCurrency(value)}
      </span>
    </div>
  );
}

function ExceedsCreditLimitContent({ approval, financedAmount }) {
  const differenceToPay = Math.max(financedAmount - Number(approval), 0);
  const amountLeftToFinance = Number(approval);

  return (
    <Alert
      showIcon
      type="warningLight"
      title="approved amount too low"
      data-testid="message-exceeds-credit-limit"
    >
      <h3 className="font-bold mb-2">Approved credit is lower than the financing amount</h3>
      <p className="mb-3">
        Good news — your application is approved! However, the approved credit limit doesn&apos;t
        fully cover the amount you wanted to finance.
      </p>

      <div className="bg-white rounded-md p-3 my-3 border border-gray-200">
        <AmountRow label="Total amount to be financed" value={financedAmount} />
        <AmountRow label="Approved credit limit" value={amountLeftToFinance} />
        <AmountRow label="Balance to pay by credit card" value={differenceToPay} highlight />
      </div>

      <p className="mt-3">
        You can pay the balance of{' '}
        <span className="font-bold">{formatCurrency(differenceToPay)}</span> using a credit card,
        and proceed with financing for the remaining{' '}
        <span className="font-bold">{formatCurrency(amountLeftToFinance)}</span>.
      </p>
    </Alert>
  );
}

function FinancingTooLowContent({ already_paid_amount, amount_to_pay }) {
  return (
    <Alert
      showIcon
      type="warningLight"
      title="correction required"
      data-testid="message-not-eligible"
    >
      <h3 className="font-bold">Financing amount too low!</h3>
      <span>Your application is approved. However, you have an available credit of&nbsp;</span>
      <span className="font-bold">({formatCurrency(already_paid_amount)})</span>
      <span>&nbsp;so the amount to be financed&nbsp;</span>
      <span className="font-bold">({formatCurrency(amount_to_pay - already_paid_amount)})</span>
      <span>
        &nbsp;is lower than the minimum eligible amount that can be financed. You can click on the
        button below to complete the remaining amount instead of financing it.
      </span>
    </Alert>
  );
}

export default function FinancingNotEligible({
  already_paid_amount,
  amount_to_pay,
  exceedsCreditLimit,
  approval,
  financedAmount,
  routes,
}) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const onOpenConfirmationPopup = () => {
    setIsModalOpen(true);
  };
  const onCollectPayment = () => {
    const url = generateURL({ url: routes?.payment });
    window.open(url, '_self');
  };

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  return (
    <div>
      <Row className="justify-center flex-col items-center w-full">
        <Column className="font-regular text-large w-full">
          {exceedsCreditLimit ? (
            <ExceedsCreditLimitContent approval={approval} financedAmount={financedAmount} />
          ) : (
            <FinancingTooLowContent
              already_paid_amount={already_paid_amount}
              amount_to_pay={amount_to_pay}
            />
          )}
        </Column>
        <Column className="text-center text-gray-400 w-1/3">
          <PrimaryButton data-testid="btn-collect-payment" block onClick={onOpenConfirmationPopup}>
            {exceedsCreditLimit ? 'Pay Balance & Continue' : 'Collect Payment'}
          </PrimaryButton>
        </Column>
      </Row>
      <Modal isOpen={isModalOpen}>
        <h3 className="font-bold text-center">
          Are you sure you want to proceed to Payment Collection?
        </h3>
        <div className="p-6 text-center" data-testid="title-autopay-redirect">
          You will leave the financing application and be redirected to the payment collection
          portal
        </div>
        <hr></hr>
        <div className="flex items-end justify-end gap-4">
          <OutlineButton type="button" onClick={onModalClose} data-testid="btn-modal-cancel">
            Cancel
          </OutlineButton>
          <PrimaryButton type="button" onClick={onCollectPayment} data-testid="btn-modal-confirm">
            Confirm
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  );
}
